'use client';

import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { trackEvent } from '@/lib/analytics';

export default function Footer() {
  const pathname = usePathname();
  
  // Helper function to track footer clicks
  const handleFooterClick = (clickText: string, clickUrl: string) => {
    trackEvent('footer_click', {
      click_text: clickText,
      click_url: clickUrl,
      page_path: pathname
    });
  };
  return (
    <footer role="contentinfo">
      <div className="footer-content">
        {/* Top Section */}
        <div className="footer-top">
          <div className="footer-column">
            <h2 className="footer-heading">Get in touch</h2>
          </div>
          
          <div className="footer-column">
            <h3 className="footer-label">GENERAL</h3>
            <a 
              href="mailto:hello@havascx-us.com" 
              className="footer-link md:mb-6"
              onClick={() => handleFooterClick('hello@havascx-us.com', 'mailto:hello@havascx-us.com')}
            >
              hello@havascx-us.com
            </a>
            <p className="footer-address flex items-center gap-1 hidden md:flex">
              Follow us on{' '}
              <a
                href="https://www.linkedin.com/company/havas-cx"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-link font-bold inline-flex items-center"
                aria-label="Follow us on LinkedIn (opens in a new tab)"
                onClick={() => handleFooterClick('LinkedIn', 'https://www.linkedin.com/company/havas-cx')}
              >
                LinkedIn
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                  className="ml-1"
                >
                  <path
                    d="M6 14L14 6M14 6H7M14 6V13"
                    stroke="#ef4444"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </p>
          </div>
          
          <div className="footer-column">
            <h3 className="footer-label">CAREERS</h3>
            <a 
              href="mailto:careers@havascx-us.com" 
              className="footer-link"
              onClick={() => handleFooterClick('careers@havascx-us.com', 'mailto:careers@havascx-us.com')}
            >
              careers@havascx-us.com
            </a>
            <p className="flex items-center gap-1 mt-[35px] md:hidden">
              Follow us on{' '}
              <a
                href="https://www.linkedin.com/company/havas-cx"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-link font-bold inline-flex items-center"
                aria-label="Follow us on LinkedIn (opens in a new tab)"
                onClick={() => handleFooterClick('LinkedIn', 'https://www.linkedin.com/company/havas-cx')}
              >
                LinkedIn
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                  className="ml-1"
                >
                  <path
                    d="M6 14L14 6M14 6H7M14 6V13"
                    stroke="#ef4444"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </p>
          </div>
        </div>

        {/* Location Section */}
        <div className="footer-location">
          <h2 className="footer-heading">U.S. Headquarters</h2>
          <p className="footer-address">
            <a 
              href="https://www.google.com/maps/place/Havas/@40.7229489,-74.0103957,17z/data=!3m1!5s0x89c259f4bec74c0f:0x1f4d1a47344f8d3!4m14!1m7!3m6!1s0x89c259f2b887fe81:0x63cf9abc66b5d73!2sHavas!8m2!3d40.7229449!4d-74.0078208!16s%2Fg%2F1pv09bbm4!3m5!1s0x89c259f2b887fe81:0x63cf9abc66b5d73!8m2!3d40.7229449!4d-74.0078208!16s%2Fg%2F1pv09bbm4?entry=ttu&g_ep=EgoyMDI1MTIwMi4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
              onClick={() => handleFooterClick('200 Hudson St. New York, NY 10013', 'https://www.google.com/maps/place/200+Hudson+St,+New+York,+NY+10013')}
            >
              200 Hudson St. New York, NY 10013
            </a>
          </p>
        </div>

        {/* Footer Links */}
        <div className="footer-links">
          <div></div>
          <div className="footer-link-container">
            <a 
              href="https://www.havas.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="footer-link"
              onClick={() => handleFooterClick('Havas Group', 'https://www.havas.com/')}
            >
              Havas Group
            </a>
            <a 
              href="https://havascx.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="footer-link"
              onClick={() => handleFooterClick('Havas CX Network', 'https://havascx.com/')}
            >
              Havas CX Network
            </a>
            <a 
              href="https://nyc.havas.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="footer-link"
              onClick={() => handleFooterClick('Havas New York Village', 'https://nyc.havas.com/')}
            >
              Havas New York Village
            </a>
          </div>
        </div>

        {/* Logo */}
        <div className="footer-logo">
          <Image
            src="/images/logo-footer.png"
            alt="Havas CX Logo"
            width={1200}
            height={300}
            className="footer-logo-img"
          />
        </div>

        <div id="footer-white-line">

        </div>

        <div className="footer-legal-links">
          <a href="https://nyc.havas.com/terms-of-use/" className="label-xs footer-legal-link" target="_blank" rel="noopener noreferrer">Terms of Use</a>
          <a href="https://nyc.havas.com/data-protection-policy/" target="_blank" rel="noopener noreferrer" className="label-xs footer-legal-link">Data Protection Policy</a>
          <a href="https://nyc.havas.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="label-xs footer-legal-link">Privacy Policy</a>
          <a href="https://nyc.havas.com/cookies-policy/" target="_blank" rel="noopener noreferrer" className="label-xs footer-legal-link">Cookies Policy</a>
        </div>
        
        <div className="footer-legal-links-mobile">
          <div className="left-side">
            <a href="https://nyc.havas.com/terms-of-use/" className="label-xs footer-legal-link" target="_blank" rel="noopener noreferrer">Terms of Use</a>
            <a href="https://nyc.havas.com/data-protection-policy/" target="_blank" rel="noopener noreferrer" className="label-xs footer-legal-link">Data Protection Policy</a>
          </div>
          <div className="right-side">
            <a href="https://nyc.havas.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="label-xs footer-legal-link">Privacy Policy</a>
            <a href="https://nyc.havas.com/cookies-policy/" target="_blank" rel="noopener noreferrer" className="label-xs footer-legal-link">Cookies Policy</a>
          </div>
        </div>

      </div>
    </footer>
  );
}

